import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import logomark from '../../assets/logomark.svg';
import { LANGUAGES, makeTranslator, resolveLanguage } from '../../i18n/LanguageContext';

const ENDPOINT = 'https://europe-west2-agriware-operations-2d9a7.cloudfunctions.net/verifyPassport';

const LOGO = '/logo-agriware.png';

// verifyPassport returns both a machine-readable errorCode and an English
// sentence. Nobody is signed in here, so the server has no idea what language
// to answer in — translate the code and keep its text only as a backstop for
// anything a newer deploy starts returning.
const SERVER_ERROR_KEYS = {
  reference_required: 'verify.err_reference_required',
  rate_limited: 'verify.err_rate_limited',
  not_found: 'verify.err_not_found',
  code_mismatch: 'verify.err_code_mismatch',
  unknown_section: 'verify.err_unknown_section',
  read_failed: 'verify.err_read_failed',
};

const C = {
  text: '#2C2C2A',
  secondary: '#5F5E5A',
  tertiary: '#888780',
  bg: '#F1EFE8',
  border: '#D3D1C7',
  hair: '#E8E6DE',
  slate: '#374550',
  orange: '#FF6A00',
  danger: '#A32D2D',
  warning: '#854F0B',
  success: '#3B6D11',
};

const fmtDate = (iso, locale) =>
  iso ? new Date(iso).toLocaleDateString(locale, { day: 'numeric', month: 'long', year: 'numeric' }) : '—';

const fmtDateTime = (iso, locale) =>
  iso ? new Date(iso).toLocaleString(locale, {
    day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '—';

function Row({ label, value, mono, colour }) {
  return (
    <div style={{ display: 'flex', padding: '9px 0', borderBottom: `0.5px solid ${C.hair}`, gap: 16 }}>
      <div style={{ fontSize: 13, color: C.secondary, width: 150, flexShrink: 0 }}>{label}</div>
      <div style={{
        fontSize: 13, color: colour || C.text, wordBreak: 'break-word',
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
      }}>{value}</div>
    </div>
  );
}

function Panel({ children, style }) {
  return (
    <div style={{
      background: '#fff', border: `0.5px solid ${C.border}`, borderRadius: 8,
      padding: '20px 22px', marginBottom: 16, ...style,
    }}>{children}</div>
  );
}

function ShowMore({ shown, total, busy, onClick, t }) {
  return (
    <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
      <button
        type="button"
        onClick={onClick}
        disabled={busy}
        style={{
          fontSize: 12, padding: '7px 14px', borderRadius: 6, cursor: busy ? 'default' : 'pointer',
          border: `0.5px solid ${C.border}`, background: '#fff', color: C.secondary,
        }}
      >
        {busy ? t('common.loading') : t('verify.show_more')}
      </button>
      <span style={{ fontSize: 11, color: C.tertiary }}>{t('verify.showing_of').replace('{shown}', shown).replace('{total}', total)}</span>
    </div>
  );
}

// Photos and video carried on a record. Media is opened in a new tab rather
// than lightboxed - this page is read by strangers on unknown devices.
function Media({ items }) {
  if (!items || !items.length) return null;
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
      {items.map((m, i) => (m.type === 'video' ? (
        <video key={i} src={m.url} controls preload="metadata"
          style={{ width: 220, borderRadius: 6, border: `0.5px solid ${C.border}` }} />
      ) : (
        <a key={i} href={m.url} target="_blank" rel="noopener noreferrer">
          <img src={m.url} alt="" loading="lazy"
            style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6, border: `0.5px solid ${C.border}` }} />
        </a>
      )))}
    </div>
  );
}

const Photos = ({ urls }) => <Media items={(urls || []).map((u) => ({ url: u, type: 'photo' }))} />;

function Chevron({ open }) {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"
      style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', flexShrink: 0 }}>
      <path d="M3 1 L7 5 L3 9" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function PassportVerify() {
  // Nobody is signed in on this page, so there is no organisation language to
  // inherit. Default to the browser's and let the reader change it — a
  // verification page that cannot be read is no use to the person checking.
  const [language, setLanguage] = useState(() =>
    resolveLanguage(typeof navigator !== 'undefined' ? navigator.language : ''));
  const t = useMemo(() => makeTranslator(language), [language]);
  const { reference: routeRef } = useParams();
  const [params] = useSearchParams();

  const [reference, setReference] = useState(routeRef || params.get('reference') || '');
  const [orgCode, setOrgCode] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checked, setChecked] = useState(false);
  // Extra pages fetched by the "show more" controls, appended per section.
  const [more, setMore] = useState({});
  const [paging, setPaging] = useState(null);
  // A check's items stay hidden until the reader asks for them.
  const [openChecks, setOpenChecks] = useState({});
  const [openServices, setOpenServices] = useState({});

  const lookup = useCallback(async (ref, code) => {
    if (!ref.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const url = new URL(ENDPOINT);
      url.searchParams.set('reference', ref.trim());
      if (code && code.trim()) url.searchParams.set('orgCode', code.trim());
      const res = await fetch(url);
      const json = await res.json();
      if (!json.ok) {
        const key = SERVER_ERROR_KEYS[json.errorCode];
        setError(key ? t(key) : (json.error || t('verify.err_not_verified')));
        setResult(null);
      } else {
        setResult(json);
        setMore({});
        setOpenChecks({});
        setOpenServices({});
      }
    } catch (e) {
      setError(t('verify.err_unreachable'));
      setResult(null);
    }
    setChecked(true);
    setLoading(false);
  }, [t]);

  // Each "show more" is its own request, so verifyLog records how far a caller
  // went rather than one entry standing for the whole history.
  const loadMore = useCallback(async (section) => {
    const state = more[section] || { page: 1, items: [], hasMore: true };
    setPaging(section);
    try {
      const url = new URL(ENDPOINT);
      url.searchParams.set('reference', reference.trim());
      url.searchParams.set('orgCode', orgCode.trim());
      url.searchParams.set('section', section);
      url.searchParams.set('page', String(state.page + 1));
      const res = await fetch(url);
      const json = await res.json();
      if (json.ok) {
        setMore((prev) => {
          const cur = prev[section] || { page: 1, items: [], hasMore: true };
          return {
            ...prev,
            [section]: {
              page: json.page,
              items: [...cur.items, ...(json.items || [])],
              hasMore: !!json.hasMore,
            },
          };
        });
      }
    } catch (e) {
      /* leave the control in place so the reader can retry */
    }
    setPaging(null);
  }, [more, reference, orgCode]);

  // Rows already on screen for a section: the first page plus anything paged in.
  const rowsFor = (section, firstPage) => [...(firstPage || []), ...((more[section] || {}).items || [])];
  const hasMoreFor = (section, initialHasMore) =>
    more[section] ? more[section].hasMore : !!initialHasMore;

  // A scanned QR code carries the reference, so the public check runs on arrival.
  useEffect(() => {
    if (routeRef) lookup(routeRef, '');
  }, [routeRef, lookup]);

  const full = result && result.tier === 'full';

  return (
    <div style={{
      position: 'relative',
      minHeight: '100vh',
      background: result && !result.revoked ? '#EEF5EC' : error || result?.revoked ? '#FBEFEF' : C.bg,
      transition: 'background 0.5s ease',
      fontFamily: 'var(--font-primary, Helvetica, Arial, sans-serif)',
      padding: '32px 20px 64px',
      overflow: 'hidden',
    }}>
      {/* Same tiled mark as the sign-in pages — this is the one page a
          stranger sees, so it should read as ours. */}
      <div
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: '-40%',
          backgroundImage: `url(${logomark})`,
          backgroundRepeat: 'repeat',
          backgroundSize: '92px auto',
          transform: 'rotate(-45deg)',
          transformOrigin: 'center',
          // Desaturated so the marks take the background's colour rather than
          // reading as grey blotches on the green or red.
          filter: 'grayscale(1)',
          opacity: 0.14,
          mixBlendMode: 'multiply',
          pointerEvents: 'none',
        }}
      />
      <div style={{ position: 'relative', maxWidth: 680, margin: '0 auto' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <img src={LOGO} alt="Agriware" style={{ height: 26, width: 'auto' }}
               onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          <div style={{ fontSize: 15, color: C.slate, letterSpacing: 0.4 }}>
            <strong style={{ fontWeight: 600 }}>AGRI</strong>WARE
          </div>
        </div>
        {/* Detected from the browser, but a verifier who cannot read the page is
            the one person it exists for, so let them switch. */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 10 }}>
          <label style={{ fontSize: 11, color: C.tertiary, display: 'flex', alignItems: 'center', gap: 6 }}>
            {t('verify.language')}
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              style={{
                fontSize: 12, padding: '3px 6px', borderRadius: 4,
                border: `0.5px solid ${C.border}`, background: '#fff', color: C.text,
              }}
            >
              {LANGUAGES.map((l) => (
                <option key={l.code} value={l.code}>{l.label}</option>
              ))}
            </select>
          </label>
        </div>
        <div style={{ fontSize: 20, color: C.text, marginBottom: 4 }}>{t('verify.title')}</div>
        <div style={{ fontSize: 13, color: C.secondary, marginBottom: 24, lineHeight: 1.6 }}>
          {t('verify.intro')}
        </div>

        <Panel>
          <label style={{ display: 'block', fontSize: 11, color: C.secondary, marginBottom: 6 }}>
            {t('verify.reference_label')}
          </label>
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="L35-XXXX-20260825-XXXX"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 14,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              border: `0.5px solid ${C.border}`, borderRadius: 6, marginBottom: 14,
            }}
          />

          <label style={{ display: 'block', fontSize: 11, color: C.secondary, marginBottom: 6 }}>
            {t('verify.orgcode_label')} <span style={{ color: C.tertiary }}>{t('verify.orgcode_optional')}</span>
          </label>
          <input
            value={orgCode}
            onChange={(e) => setOrgCode(e.target.value)}
            placeholder="XXX-XXX-XXX"
            style={{
              width: '100%', boxSizing: 'border-box', padding: '9px 11px', fontSize: 14,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              border: `0.5px solid ${C.border}`, borderRadius: 6, marginBottom: 16,
            }}
          />

          <button
            onClick={() => lookup(reference, orgCode)}
            disabled={loading || !reference.trim()}
            style={{
              background: C.slate, color: '#fff', border: 'none', borderRadius: 6,
              padding: '10px 20px', fontSize: 13, fontFamily: 'inherit',
              cursor: loading || !reference.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !reference.trim() ? 0.5 : 1,
            }}
          >
            {loading ? t('verify.checking') : t('verify.submit')}
          </button>
        </Panel>

        {error && (
          <Panel style={{ borderLeft: `3px solid ${C.danger}` }}>
            <div style={{ fontSize: 14, color: C.danger, marginBottom: 6 }}>{t('verify.not_verified')}</div>
            <div style={{ fontSize: 13, color: C.secondary, lineHeight: 1.6 }}>{error}</div>
          </Panel>
        )}

        {result && result.revoked && (
          <Panel style={{ borderLeft: `3px solid ${C.danger}` }}>
            <div style={{ fontSize: 14, color: C.danger, marginBottom: 6 }}>{t('verify.withdrawn')}</div>
            <div style={{ fontSize: 13, color: C.secondary, lineHeight: 1.6 }}>
              This passport was issued but has since been withdrawn by the issuer. It should not
              be relied upon.
            </div>
          </Panel>
        )}

        {result && !result.revoked && (
          <>
            <Panel style={{ borderLeft: `3px solid ${result.superseded ? C.warning : C.success}` }}>
              <div style={{ fontSize: 14, color: result.superseded ? C.warning : C.success, marginBottom: 10 }}>
                {result.superseded ? t('verify.genuine_superseded') : t('verify.genuine')}
              </div>
              {result.superseded && (
                <div style={{ fontSize: 13, color: C.secondary, lineHeight: 1.6, marginBottom: 12 }}>
                  A more recent passport has been issued for this machine
                  (<span style={{ fontFamily: 'ui-monospace, monospace' }}>{result.supersededBy}</span>).
                  The document you hold reflects the machine as at its issue date.
                </div>
              )}
              <Row label={t('verify.row_reference')} value={result.reference} mono />
              <Row label={t('verify.row_issued_by')} value={result.issuerName || '—'} />
              <Row label={t('verify.row_issued_on')} value={fmtDate(result.issuedOn, language)} />
              <Row label={t('machines.col_machine')} value={result.machineTitle || '—'} />
              <Row label={t('machines.serial_number')} value={result.machineSerial || t('verify.not_recorded')}
                   colour={result.machineSerial ? undefined : C.tertiary} mono={!!result.machineSerial} />
              <div style={{ fontSize: 11, color: C.tertiary, marginTop: 14, lineHeight: 1.6 }}>
                {t('verify.fingerprint')}<br />
                <span style={{ fontFamily: 'ui-monospace, monospace', wordBreak: 'break-all' }}>
                  {result.sha256}
                </span>
              </div>
            </Panel>

            {!full && (
              <Panel style={{ background: '#FAFAF8' }}>
                <div style={{ fontSize: 13, color: C.secondary, lineHeight: 1.6 }}>
                  Add the issuing organisation's code above to see this machine's full record —
                  its current status, defect history with written release statements, and its
                  inspection record.
                </div>
              </Panel>
            )}
          </>
        )}

        {full && (
          <>
            <Panel>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 12 }}>Machine</div>
              <Row label={t('verify.row_make_model')} value={result.machine.title} />
              <Row label={t('machines.category')} value={result.machine.category || '—'} />
              <Row label={t('machines.serial_number')} value={result.machine.serial || t('verify.not_recorded')} mono={!!result.machine.serial} />
              <Row label={t('machines.registration')} value={result.machine.registration || '—'} />
              <Row label={t('machines.engine_hours')} value={result.machine.currentHours != null ? `${result.machine.currentHours.toLocaleString('en-GB')} h` : '—'} />
              <Row label={t('verify.row_status')}
                   value={result.machine.isBlocked ? t('verify.blocked_from_use') : t('verify.in_service')}
                   colour={result.machine.isBlocked ? C.danger : C.success} />
            </Panel>

            <Panel>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>{t('dashboard.open_defects')}</div>
              {result.openDefects.length === 0 ? (
                <div style={{ fontSize: 13, color: C.tertiary, marginTop: 8 }}>
                  {t('verify.no_open_defects')}
                </div>
              ) : rowsFor('defects-open', result.openDefects).map((d, i) => (
                <div key={i} style={{
                  marginTop: 10, padding: '10px 12px', borderRadius: 6,
                  border: `0.5px solid ${d.blocking ? '#F09595' : C.border}`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                    <div style={{ fontSize: 13, color: C.text }}>{d.description}</div>
                    <div style={{ fontSize: 11, color: d.blocking ? C.danger : C.tertiary, flexShrink: 0 }}>
                      {d.blocking ? t('verify.blocking') : d.severity}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: C.tertiary, marginTop: 4 }}>
                    {t('verify.raised')} {fmtDate(d.reportedAt, language)}{d.reportedBy ? ` · ${d.reportedBy}` : ''}
                  </div>
                  <Photos urls={d.photoUrls} />
                </div>
              ))}
              {hasMoreFor('defects-open', result.openDefectsHasMore) && result.openDefects.length > 0 && (
                <ShowMore
                  shown={rowsFor('defects-open', result.openDefects).length}
                  total={result.openDefectsTotal}
                  busy={paging === 'defects-open'} t={t}
                  onClick={() => loadMore('defects-open')}
                />
              )}
            </Panel>

            <Panel>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>{t('verify.defect_history')}</div>
              {result.resolvedDefects.length === 0 ? (
                <div style={{ fontSize: 13, color: C.tertiary, marginTop: 8 }}>
                  {t('verify.no_resolved_defects')}
                </div>
              ) : rowsFor('defects-resolved', result.resolvedDefects).map((d, i) => (
                <div key={i} style={{ marginTop: 14 }}>
                  <div style={{
                    display: 'flex', justifyContent: 'space-between', gap: 12,
                    background: C.bg, padding: '8px 12px', borderRadius: '6px 6px 0 0',
                  }}>
                    <div style={{ fontSize: 13, color: C.text }}>{d.description}</div>
                    {d.blocking && (
                      <div style={{ fontSize: 11, color: C.danger, flexShrink: 0 }}>
                        Blocked · {d.blockedDays} day{d.blockedDays === 1 ? '' : 's'}
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '10px 12px', border: `0.5px solid ${C.border}`, borderTop: 'none', borderRadius: '0 0 6px 6px' }}>
                    <div style={{ fontSize: 11, color: C.tertiary, marginBottom: 6 }}>
                      {t('verify.raised')} {fmtDate(d.reportedAt, language)} · {t('verify.resolved')} {fmtDate(d.resolvedAt, language)}
                    </div>
                    {d.resolution && (
                      <div style={{ fontSize: 13, color: C.text, marginBottom: 10, lineHeight: 1.6 }}>
                        {d.resolution}
                      </div>
                    )}
                    <Photos urls={d.photoUrls} />
                    {d.narrative && (
                      <div style={{ borderLeft: `2px solid ${C.orange}`, paddingLeft: 12, background: '#FAFAF8', padding: '10px 12px' }}>
                        <div style={{ fontSize: 11, color: C.secondary, marginBottom: 5 }}>{t('defects.return_to_service')}</div>
                        <div style={{ fontSize: 13, color: C.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' }}>
                          {d.narrative}
                        </div>
                        <div style={{ fontSize: 11, color: C.tertiary, marginTop: 8, paddingTop: 8, borderTop: `0.5px solid ${C.border}` }}>
                          {[d.narrativeBy, d.narrativeByRole].filter(Boolean).join(' · ')} · {fmtDateTime(d.narrativeAt, language)}
                          {d.narrativeRevised && ` · ${t('verify.revised_after_issue')}`}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {hasMoreFor('defects-resolved', result.resolvedDefectsHasMore) && result.resolvedDefects.length > 0 && (
                <ShowMore
                  shown={rowsFor('defects-resolved', result.resolvedDefects).length}
                  total={result.resolvedDefectsTotal}
                  busy={paging === 'defects-resolved'} t={t}
                  onClick={() => loadMore('defects-resolved')}
                />
              )}
            </Panel>

            <Panel>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 12 }}>{t('verify.inspection_record')}</div>
              <Row label={t('verify.checks_recorded')} value={result.checks.total.toLocaleString('en-GB')} />
              <Row label={t('verify.pass_rate')} value={result.checks.passRate != null ? `${result.checks.passRate}%` : '—'} />
              <Row label={t('verify.defects_raised')} value={result.checks.defectsRaised.toLocaleString('en-GB')} />
              <Row label={t('verify.most_recent_check')} value={fmtDate(result.checks.lastAt, language)} />
            </Panel>

            <Panel>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>{t('verify.check_log')}</div>
              {rowsFor('checks', result.checks.recent).length === 0 ? (
                <div style={{ fontSize: 13, color: C.tertiary, marginTop: 8 }}>
                  {t('verify.no_checks')}
                </div>
              ) : rowsFor('checks', result.checks.recent).map((c, i) => (
                <div key={i} style={{ marginTop: 12, border: `0.5px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
                  <button
                    type="button"
                    aria-expanded={!!openChecks[i]}
                    onClick={() => setOpenChecks(prev => ({ ...prev, [i]: !prev[i] }))}
                    style={{
                      width: '100%', display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: 12, background: C.bg, padding: '8px 12px',
                      border: 'none', font: 'inherit', textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: 13, color: C.text }}>
                      {fmtDateTime(c.submittedAt, language)}{c.by ? ` · ${c.by}` : ''}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: c.failCount > 0 ? C.danger : C.success }}>
                        {c.failCount > 0 ? t('verify.n_failed').replace('{n}', c.failCount) : t('verify.all_passed')}
                        {c.triggeredBlock ? ` · ${t('verify.blocked_the_machine')}` : ''}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.secondary }}>
                        {openChecks[i] ? t('verify.hide') : t('verify.details')}
                        <Chevron open={!!openChecks[i]} />
                      </span>
                    </span>
                  </button>
                  {openChecks[i] && (
                  <div style={{ padding: '10px 12px', borderTop: `0.5px solid ${C.border}` }}>
                    {c.engineHours != null && (
                      <div style={{ fontSize: 11, color: C.tertiary, marginBottom: 8 }}>
                        {c.engineHours.toLocaleString(language)} h
                        {c.totalCount ? ` · ${c.totalCount} item${c.totalCount === 1 ? '' : 's'} checked` : ''}
                      </div>
                    )}
                    {(c.items || []).map((it, j) => (
                      <div key={j} style={{
                        display: 'flex', gap: 10, alignItems: 'flex-start',
                        padding: '5px 0', borderTop: j ? `0.5px solid ${C.border}` : 'none',
                      }}>
                        <div style={{
                          width: 7, height: 7, borderRadius: '50%', marginTop: 6, flexShrink: 0,
                          background: it.status === 'fail' ? C.danger : C.success,
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 12, color: it.status === 'fail' ? C.danger : C.text }}>
                            {it.label}
                            {it.category ? <span style={{ color: C.tertiary }}> · {it.category}</span> : null}
                          </div>
                          {it.notes && (
                            <div style={{ fontSize: 11, color: C.tertiary, marginTop: 2, lineHeight: 1.6 }}>{it.notes}</div>
                          )}
                          {it.photoUri && <Photos urls={[it.photoUri]} />}
                        </div>
                      </div>
                    ))}
                  </div>
                  )}
                </div>
              ))}
              {hasMoreFor('checks', result.checks.hasMore) && result.checks.recent.length > 0 && (
                <ShowMore
                  shown={rowsFor('checks', result.checks.recent).length}
                  total={result.checks.total}
                  busy={paging === 'checks'} t={t}
                  onClick={() => loadMore('checks')}
                />
              )}
            </Panel>

            <Panel>
              <div style={{ fontSize: 14, color: C.text, marginBottom: 4 }}>{t('verify.service_history')}</div>
              {result.nextService && (result.nextService.hours || result.nextService.date) && (
                <div style={{ fontSize: 11, color: C.tertiary, marginBottom: 4 }}>
                  {t('verify.next_service')}{result.nextService.hours ? ` at ${result.nextService.hours.toLocaleString(language)} h` : ''}
                  {result.nextService.date ? ` · ${fmtDate(result.nextService.date, language)}` : ''}
                </div>
              )}
              {rowsFor('services', result.services).length === 0 ? (
                <div style={{ fontSize: 13, color: C.tertiary, marginTop: 8 }}>
                  {t('verify.no_services')}
                </div>
              ) : rowsFor('services', result.services).map((r, i) => (
                <div key={i} style={{ marginTop: 12, border: `0.5px solid ${C.border}`, borderRadius: 6, overflow: 'hidden' }}>
                  <button
                    type="button"
                    aria-expanded={!!openServices[i]}
                    onClick={() => setOpenServices(prev => ({ ...prev, [i]: !prev[i] }))}
                    style={{
                      width: '100%', display: 'flex', justifyContent: 'space-between',
                      alignItems: 'center', gap: 12, background: C.bg, padding: '8px 12px',
                      border: 'none', font: 'inherit', textAlign: 'left', cursor: 'pointer',
                    }}
                  >
                    <span style={{ fontSize: 13, color: C.text, textTransform: 'capitalize' }}>{r.type}</span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                      <span style={{ fontSize: 11, color: C.tertiary }}>
                        {fmtDate(r.dateOfService, language)}
                        {r.hoursAtService != null ? ` · ${r.hoursAtService.toLocaleString(language)} h` : ''}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: C.secondary }}>
                        {openServices[i] ? t('verify.hide') : t('verify.details')}
                        <Chevron open={!!openServices[i]} />
                      </span>
                    </span>
                  </button>
                  {openServices[i] && (
                  <div style={{ padding: '10px 12px', borderTop: `0.5px solid ${C.border}` }}>
                    {r.description && (
                      <div style={{ fontSize: 13, color: C.text, lineHeight: 1.6 }}>{r.description}</div>
                    )}
                    <div style={{ fontSize: 11, color: C.tertiary, marginTop: 6 }}>
                      {[
                        r.performedBy,
                        r.external ? t('verify.external') : null,
                        r.workshopJobRef ? t('service.job_ref').replace('{ref}', r.workshopJobRef) : null,
                        r.sealed ? t('verify.sealed') : null,
                      ].filter(Boolean).join(' · ')}
                    </div>
                    {(r.parts || []).length > 0 && (
                      <div style={{ fontSize: 11, color: C.secondary, marginTop: 8 }}>
                        {t('verify.parts')}: {r.parts.map((pt) => `${pt.name}${pt.quantity ? ` ×${pt.quantity}` : ''}`).join(', ')}
                      </div>
                    )}
                    {r.notes && (
                      <div style={{ fontSize: 11, color: C.tertiary, marginTop: 6, lineHeight: 1.6 }}>{r.notes}</div>
                    )}
                    <Media items={r.media} />
                  </div>
                  )}
                </div>
              ))}
              {hasMoreFor('services', result.servicesHasMore) && (result.services || []).length > 0 && (
                <ShowMore
                  shown={rowsFor('services', result.services).length}
                  total={result.servicesTotal}
                  busy={paging === 'services'} t={t}
                  onClick={() => loadMore('services')}
                />
              )}
            </Panel>

            <Panel style={{ background: '#FAFAF8' }}>
              <div style={{ fontSize: 12, color: C.secondary, lineHeight: 1.7 }}>
                {result.note} Agriware Operations produced this record from data held by the
                issuer and does not verify the accuracy of issuer-entered information.
              </div>
            </Panel>
          </>
        )}

        {checked && !result && !error && !loading && (
          <Panel>
            <div style={{ fontSize: 13, color: C.tertiary }}>{t('verify.no_result')}</div>
          </Panel>
        )}

        <div style={{ fontSize: 11, color: C.tertiary, textAlign: 'center', marginTop: 32, lineHeight: 1.7 }}>
          Agriware Operations · agriware.uk
        </div>
      </div>
    </div>
  );
}
